import { ConfigManager } from "@/application/config/Config";
import { LoggerFactory } from "@/application/logging/Logger";
import { Pool } from "@/types";

export class PoolRepository {
  private readonly config = ConfigManager.getInstance().getApiConfig();
  private readonly logger = LoggerFactory.getLogger();
  private readonly staticBaseUrl = "https://api.swimpractice.com";

  private get token() {
    return localStorage.getItem("auth_token");
  }

  private get authHeaders() {
    return {
      Accept: "*/*",
      Authorization: `Bearer ${this.token}`,
    };
  }

  protected get jsonHeaders(): HeadersInit {
    return {
      accept: "*/*",
      "Content-Type": "application/json",
      Authorization: `Bearer ${this.token}`,
    };
  }

  private parseApiResult(json: any): { success: boolean; message: string } {
    const payload = json?.response ?? json;
    const status = Number(payload?.status ?? json?.status ?? 0);
    const message =
      String(payload?.msg ?? json?.msg ?? payload?.message ?? json?.message ?? "").trim() ||
      "Request failed";
    return { success: status === 1, message };
  }

  private resolvePoolImage(raw: unknown): string {
    const value = String(raw ?? "").trim();
    if (!value) return "/pool-placeholder.jpg";
    if (/^https?:\/\//i.test(value)) return value;
    if (value.startsWith("/")) return `${this.staticBaseUrl}${value}`;
    if (value.includes("/")) return `${this.staticBaseUrl}/${value.replace(/^\/+/, "")}`;
    return `${this.staticBaseUrl}/uploads/pool/${value}`;
  }

  async uploadPoolImage(userId: number, file: File): Promise<{ success: boolean; message: string; image_link?: string; file_name?: string }> {
    const url = `${this.config.baseUrl}/pool/upload_image`;

    try {
      const formData = new FormData();
      formData.append("user_id", String(userId));
      formData.append("image", file);

      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${this.token}`,
          "X-API-Key": this.config.apiKey,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`uploadPoolImage failed (${response.status})`);
      }

      const json = await response.json();
      const payload = json?.response ?? json;
      const parsed = this.parseApiResult(json);

      return {
        ...parsed,
        image_link: payload?.image_link,
        file_name: payload?.file_name,
      };
    } catch (err) {
      this.logger.error("Failed to upload pool image", err as Error);
      return { success: false, message: "Failed to upload pool image" };
    }
  }

  // --------------------------------------------------
  // GET ALL POOLS
  // --------------------------------------------------
  async getPoolList(
  page: number,
  pageSize: number
): Promise<Pool[]> {
  const url = `${this.config.baseUrl}/pool/list?page=${page}&pageSize=${pageSize}`;

  try {
    const response = await fetch(url);
    const json = await response.json();

    const list = json?.response ?? [];

    return list.map((p: any): Pool => ({
      id: Number(p.id),
      poolname: p.poolname ?? "",
      city: p.city ?? "",
      state: p.state ?? "",
      type: p.type ?? "",
      timing: p.timing ?? "",
      about: p.about ?? undefined,

      image: this.resolvePoolImage(
        p.image ?? p.image_link ?? p.pool_image ?? p.pool_img
      ),

      image_alt: p.image_alt ?? "",

      lat: p.pool_latitude ? Number(p.pool_latitude) : 0,
      lng: p.pool_longitude ? Number(p.pool_longitude) : 0,
      googlemap_url: p.googlemap_url ?? "", 
      price_per_session: p.price_per_session
        ? Number(p.price_per_session)
        : undefined,

      pool_length_m: p.pool_length_m
        ? Number(p.pool_length_m)
        : undefined,

      depth_m: p.depth_m ?? undefined,

      amenities: p.amenities
        ? String(p.amenities).split(",").map((a: string) => a.trim())
        : undefined,

      rating: p.rating ? Number(p.rating) : undefined,

      is_verified: p.is_verified === "1",
      status: p.status ?? "active",

      meta_title: p.meta_title ?? undefined,
      meta_description: p.meta_description ?? undefined,
      meta_keywords: p.meta_keywords
        ? String(p.meta_keywords).split(",").map((k: string) => k.trim())
        : undefined,

      created_at: p.created_at,
      updated_at: p.updated_at,
      views: Number(p.views ?? 0),
    }));
  } catch (err) {
    this.logger.error("Failed to fetch pools", err as Error);
    return [];
  }
}
async getPoolById(id: number): Promise<Pool | null> {
  const url = `${this.config.baseUrl}/pool/list?id=${id}`;

  try {
    const response = await fetch(url);
    const json = await response.json();

    const list = json?.response ?? [];
    if (!Array.isArray(list) || list.length === 0) return null;

    const p = list[0];

    return {
      id: Number(p.id),
      poolname: p.poolname ?? "",
      city: p.city ?? "",
      state: p.state ?? "",
      type: p.type ?? "",
      timing: p.timing ?? "",
      about: p.about ?? undefined,

      image: this.resolvePoolImage(
        p.image ?? p.image_link ?? p.pool_image ?? p.pool_img
      ),

      image_alt: p.image_alt ?? "",

      lat: p.pool_latitude ? Number(p.pool_latitude) : 0,
      lng: p.pool_longitude ? Number(p.pool_longitude) : 0,
       googlemap_url: p.googlemap_url ?? "",
      price_per_session: p.price_per_session
        ? Number(p.price_per_session)
        : undefined,

      pool_length_m: p.pool_length_m
        ? Number(p.pool_length_m)
        : undefined,

      depth_m: p.depth_m ?? undefined,

      amenities: p.amenities
        ? String(p.amenities).split(",").map((a: string) => a.trim())
        : undefined,

      rating: p.rating ? Number(p.rating) : undefined,
      is_verified: p.is_verified === "1",

      status: p.status ?? "active",
      created_at: p.created_at,
      updated_at: p.updated_at,
      views: Number(p.views ?? 0),
    };
  } catch (err) {
    this.logger.error("Failed to fetch pool by id", err as Error);
    return null;
  }
}


  // --------------------------------------------------
  // GET POOL DETAIL
  // API: GET /api/pool/detail?id=ID
  // --------------------------------------------------
  async getPoolDetail(poolId: number): Promise<any | null> {
    const url = `${this.config.baseUrl}/pool/detail?id=${poolId}`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: this.authHeaders,
      });

      const json = await response.json();
      return json?.data ?? null;
    } catch (err) {
      this.logger.error("Failed to fetch pool detail", err as Error, { poolId });
      return null;
    }
  }

  private mapPoolToCreatePayload(pool: Partial<Pool>) {
    return {
      poolname: pool.poolname ?? "",
      coordinates:
        pool.lat && pool.lng
          ? `${pool.lat}, ${pool.lng}`
          : null,

      pool_latitude: pool.lat ? String(pool.lat) : null,
      pool_longitude: pool.lng ? String(pool.lng) : null,

      phone_number: pool.contact_phone ?? null,
      googlemap_url: pool.googlemap_url ?? null,
      website_url: null,

      image: pool.image ?? null,
      image_alt: pool.image_alt ?? null,

      address: null,
      about: pool.about ?? null,

      city: pool.city ?? null,
      state: pool.state ?? null,
      type: pool.type ?? null,
      timing: pool.timing ?? null,

      price_per_session:
        pool.price_per_session != null
          ? String(pool.price_per_session)
          : null,

      pool_length_m:
        pool.pool_length_m != null
          ? String(pool.pool_length_m)
          : null,

      depth_m: pool.depth_m ?? null,

      amenities: pool.amenities
        ? pool.amenities.join(", ")
        : null,

      rating:
        pool.rating != null
          ? String(pool.rating)
          : null,

      is_verified: pool.is_verified ? "1" : "0",
      tags: pool.tags ? pool.tags.join(",") : null,

      createdBy: pool.createdBy ?? null,
      user_id:
        pool.createdBy != null
          ? String(pool.createdBy)
          : null,
      status: pool.status ?? "active",

      meta_title: pool.meta_title ?? null,
      meta_description: pool.meta_description ?? null,
      meta_keywords: pool.meta_keywords
        ? pool.meta_keywords.join(",")
        : null,

      email_address: ""
    };
  }

  // --------------------------------------------------
  // CREATE POOL 
  // API: POST /api/pool/create
  // --------------------------------------------------
async createPool(pool: Partial<Pool>): Promise<{ success: boolean; message: string }> {
  const url = `${this.config.baseUrl}/pool/create`;

  const payload = this.mapPoolToCreatePayload(pool);

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: this.jsonHeaders,
      body: JSON.stringify(payload),
    });

    const json = await response.json();
    return this.parseApiResult(json);
  } catch (err) {
    this.logger.error("Failed to create pool", err as Error, payload);
    return { success: false, message: "Failed to create pool" };
  }
}



  // --------------------------------------------------
  // UPDATE POOL
  // API: POST /api/pool/update
  // --------------------------------------------------
async updatePool(pool: Partial<Pool> & { id: number }): Promise<{ success: boolean; message: string }> {
  const url = `${this.config.baseUrl}/pool/update`;

  const payload = {
    id: pool.id,
    ...this.mapPoolToCreatePayload(pool),
  };

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: this.jsonHeaders,
      body: JSON.stringify(payload),
    });

    const json = await response.json();
    return this.parseApiResult(json);
  } catch (err) {
    this.logger.error("Failed to update pool", err as Error, payload);
    return { success: false, message: "Failed to update pool" };
  }
}


  // --------------------------------------------------
  // DELETE POOL 
  // API: DELETE /api/pool/delete_pool?pool_id=ID&user_id=ID
  // --------------------------------------------------
  async deletePool(poolId: number, userId: string | number): Promise<boolean> {
    const url = `${this.config.baseUrl}/pool/delete_pool?pool_id=${encodeURIComponent(String(poolId))}&user_id=${encodeURIComponent(String(userId))}`;

    try {
      const response = await fetch(url, {
        method: "DELETE",
        headers: this.authHeaders,
      });

      const text = await response.text();
      if (!text) return false;

      const json = JSON.parse(text);
      const payload = json?.response ?? json ?? {};
      return Number(payload?.status) === 1;
    } catch (err) {
      this.logger.error("Failed to delete pool", err as Error, { poolId, userId });
      return false;
    }
  }
}
